import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
    MagnifyingGlass,
    SpinnerGap,
    User,
} from '@phosphor-icons/react';
import { searchDirectory } from '../services/directoryApi';
import CustomerDetailSheet from '../components/CustomerDetailSheet';
import { useAuth } from '../context/AuthContext';

const CustomerDirectoryPage = () => {
    const { can } = useAuth();
    const isLimited = !can('customers.read_all') && can('customers.read_limited');
    const [searchParams] = useSearchParams();
    const [q, setQ] = useState('');
    const [statusFilter, setStatusFilter] = useState(null);
    const [results, setResults] = useState([]);
    const [mode, setMode] = useState('full');
    const [loading, setLoading] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [sheetOpen, setSheetOpen] = useState(false);
    const searchTimer = useRef(null);
    const searchInputRef = useRef(null);

    // Si se entra con ?customer=ID, abre directamente la ficha de ese cliente.
    useEffect(() => {
        const customerId = searchParams.get('customer');
        if (customerId) {
            setSelectedId(customerId);
            setSheetOpen(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const runSearch = async (query, status) => {
        // En modo limitado, no buscamos con query vacío (no se permite listar).
        if (isLimited && !query) {
            setResults([]);
            setMode('limited');
            return;
        }
        setLoading(true);
        try {
            const res = await searchDirectory(query, status, isLimited ? 3 : 80);
            setResults(res.data.results || []);
            setMode(res.data.mode || (isLimited ? 'limited' : 'full'));
        } catch (err) {
            if (err.response?.status === 403) {
                toast.error('No tienes permiso para usar el directorio');
            } else {
                toast.error('Error al buscar — intenta de nuevo');
            }
            setResults([]);
        } finally { setLoading(false); }
    };

    useEffect(() => {
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            runSearch(q.trim(), statusFilter);
        }, 280);
        return () => clearTimeout(searchTimer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q, statusFilter]);

    useEffect(() => { searchInputRef.current?.focus(); }, []);

    const handleOpen = (customerId) => {
        setSelectedId(customerId);
        setSheetOpen(true);
    };

    const handleSheetClose = (open) => {
        setSheetOpen(open);
        if (!open) {
            // Refresh list to reflect any changes made inside the sheet
            runSearch(q.trim(), statusFilter);
        }
    };

    const totalCertsFound = useMemo(
        () => results.reduce((sum, r) => sum + (r.summary?.total_certificates || 0), 0),
        [results]
    );

    return (
        <div className="space-y-6" data-testid="customer-directory-page">
            <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#8B8474] font-semibold mb-1">
                    Atención a Clientes
                </p>
                <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#1F2D4B]">
                    Directorio de clientes
                </h1>
                <p className="text-sm text-[#4B5878] mt-1">
                    {isLimited
                        ? 'Busca por nombre, correo, teléfono o folio EXACTO del certificado.'
                        : 'Busca por nombre, correo, teléfono, RFC o folio del certificado. Haz clic en un cliente para abrir su expediente.'}
                </p>
            </div>

            <div className="bg-white border border-[#ECE3D2] rounded-2xl p-4 sm:p-5 shadow-sm space-y-3">
                <div className="relative">
                    <MagnifyingGlass size={20} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8B8474]" />
                    <input
                        ref={searchInputRef}
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder={isLimited
                            ? 'Buscar… nombre, correo, teléfono o folio exacto (ES4-260622-0001)'
                            : 'Buscar… nombre, correo, teléfono, RFC o folio (CC-...)'}
                        data-testid="directory-search-input"
                        className="w-full h-14 pl-12 pr-12 rounded-xl border border-[#ECE3D2] bg-white text-[#1F2D4B] text-base focus:outline-none focus:ring-2 focus:ring-[#1F2D4B]/20 focus:border-[#1F2D4B] placeholder:text-[#8B8474]"
                    />
                    {loading && (
                        <SpinnerGap size={20} className="absolute right-4 top-1/2 -translate-y-1/2 animate-spin text-[#FB7185]" />
                    )}
                    {!loading && q && (
                        <button
                            type="button"
                            onClick={() => setQ('')}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8B8474] hover:text-[#1F2D4B] text-sm font-medium"
                            data-testid="clear-search-btn"
                        >
                            Limpiar
                        </button>
                    )}
                </div>

                {/* Status filter chips: ONLY for full-access users */}
                {!isLimited && (
                    <div className="flex flex-wrap gap-2 items-center text-xs">
                        <span className="text-[#8B8474] uppercase tracking-wider font-semibold text-[10px] mr-1">Filtrar:</span>
                        {[
                            { key: null, label: 'Todos' },
                            { key: 'active', label: 'Activos' },
                            { key: 'expired', label: 'Vencidos' },
                            { key: 'used', label: 'Usados' },
                            { key: 'transferred', label: 'Transferidos' },
                            { key: 'cancelled', label: 'Cancelados' },
                        ].map((opt) => (
                            <button
                                key={opt.key || 'all'}
                                type="button"
                                onClick={() => setStatusFilter(opt.key)}
                                data-testid={`filter-${opt.key || 'all'}`}
                                className={`px-3 h-8 rounded-full border transition-colors ${
                                    statusFilter === opt.key
                                        ? 'bg-[#1F2D4B] text-white border-[#1F2D4B]'
                                        : 'bg-white text-[#4B5878] border-[#ECE3D2] hover:border-[#1F2D4B]'
                                }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                        <span className="ml-auto text-[#8B8474]">
                            {results.length} cliente(s) · {totalCertsFound} certificado(s)
                        </span>
                    </div>
                )}
                {isLimited && results.length > 0 && (
                    <p className="text-[11px] text-[#8B8474]" data-testid="limited-mode-hint">
                        {results.length} {results.length === 1 ? 'resultado' : 'resultados'}
                    </p>
                )}
            </div>

            {/* Customer list — clicking opens detail sheet */}
            <div
                className="bg-white border border-[#ECE3D2] rounded-2xl overflow-hidden"
                data-testid="customer-list"
            >
                {results.length === 0 && !loading && (
                    <div className="p-10 text-center text-sm text-[#8B8474] flex flex-col items-center gap-3">
                        <User size={32} weight="duotone" className="text-[#ECE3D2]" />
                        {q
                            ? `Sin resultados para "${q}"`
                            : (isLimited
                                ? 'Escribe el nombre completo, correo, teléfono o folio exacto del certificado para localizar al cliente.'
                                : 'Empieza escribiendo para buscar clientes…')}
                    </div>
                )}
                {loading && results.length === 0 && (
                    <div className="p-10 text-center text-sm text-[#8B8474] flex items-center justify-center gap-2">
                        <SpinnerGap size={16} className="animate-spin" /> Buscando…
                    </div>
                )}
                <ul className="divide-y divide-[#ECE3D2]">
                    {results.map((r) => (
                        <li key={r.customer.id}>
                            <button
                                type="button"
                                onClick={() => handleOpen(r.customer.id)}
                                data-testid={`customer-row-${r.customer.id}`}
                                className="w-full text-left p-4 sm:px-6 hover:bg-[#F9F7F2] transition-colors flex items-center gap-4"
                            >
                                <div className="w-11 h-11 rounded-full bg-[#1F2D4B] text-white text-base font-semibold flex items-center justify-center flex-shrink-0">
                                    {r.customer.name?.[0]?.toUpperCase() || '?'}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-[#1F2D4B] truncate">{r.customer.name || '—'}</p>
                                    <p className="text-xs text-[#4B5878] truncate">
                                        {r.customer.email || 'Sin correo'} {r.customer.phone ? ` · ${r.customer.phone}` : ''}
                                    </p>
                                    {!isLimited && (r.customer.rfc || r.summary.total_certificates) && (
                                        <p className="text-[10px] text-[#8B8474] truncate mt-0.5">
                                            {r.customer.rfc && <>RFC {r.customer.rfc} · </>}
                                            {r.summary.total_certificates} certif. · Total {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 }).format(r.summary.total_spent || 0)}
                                        </p>
                                    )}
                                    {isLimited && r.customer.rfc && (
                                        <p className="text-[10px] text-[#8B8474] truncate mt-0.5">
                                            RFC {r.customer.rfc}
                                        </p>
                                    )}
                                </div>
                                {!isLimited && (
                                    <div className="hidden sm:flex items-center gap-1.5 text-[10px] flex-shrink-0">
                                        {r.summary.active > 0 && (
                                            <span className="px-2 py-0.5 rounded-full bg-[#D1FAE5] text-[#047857] font-semibold">{r.summary.active} act.</span>
                                        )}
                                        {r.summary.used > 0 && (
                                            <span className="px-2 py-0.5 rounded-full bg-[#E0E7FF] text-[#3730A3] font-semibold">{r.summary.used} usado</span>
                                        )}
                                        {r.summary.expired > 0 && (
                                            <span className="px-2 py-0.5 rounded-full bg-[#FEE2E2] text-[#B91C1C] font-semibold">{r.summary.expired} venc.</span>
                                        )}
                                        {r.summary.transferred > 0 && (
                                            <span className="px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] font-semibold">{r.summary.transferred} transf.</span>
                                        )}
                                        {r.summary.cancelled > 0 && (
                                            <span className="px-2 py-0.5 rounded-full bg-[#F3F4F6] text-[#6B7280] font-semibold">{r.summary.cancelled} canc.</span>
                                        )}
                                    </div>
                                )}
                            </button>
                        </li>
                    ))}
                </ul>
            </div>

            <CustomerDetailSheet
                customerId={selectedId}
                open={sheetOpen}
                onOpenChange={handleSheetClose}
            />
        </div>
    );
};

export default CustomerDirectoryPage;
